import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Compass, Layers, Network, Activity, ClipboardCheck, ShieldAlert } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  PRINCIPLES, PILLARS, CHAINS, PATTERNS, TESTS, PRECAUTIONS, findChain, findTest,
} from "@/data/mioMatrix";

/**
 * MatrixRationalTab — Matriz Racional da MIO Matrix Library.
 *
 * Suporta navegação contextual via props `initialSection` + `initialId`
 * (vindas de "Ver no Matrix" no ClinicalReasoningPanel). Quando definidas:
 *  - Ativa a sub-tab correta (chains/patterns/tests/...)
 *  - Faz scrollIntoView suave no card alvo
 *  - Aplica glow azul subtil ~2s para chamar atenção
 *
 * 5 sub-tabs filtráveis por search. Sem chamadas backend, sem ML — apenas
 * leitura estruturada do JSON em `data/mioMatrix.js`.
 */

const SUB_TABS = [
  { key: "principles", label: "Princípios",       icon: Compass,  data: PRINCIPLES },
  { key: "pillars",    label: "Pilares",          icon: Layers,   data: PILLARS },
  { key: "chains",     label: "Cadeias",          icon: Network,  data: CHAINS },
  { key: "patterns",   label: "Padrões",          icon: Activity, data: PATTERNS },
  { key: "tests",      label: "Testes",           icon: ClipboardCheck, data: TESTS },
  { key: "precautions", label: "Precauções",     icon: ShieldAlert, data: PRECAUTIONS },
];

function matchesQ(it, q) {
  if (!q) return true;
  const hay = [
    it.title, it.description, it.body, it.function,
    ...(it.regions || []), ...(it.compensations || []),
    ...(it.chains || []), ...(it.patterns || []),
    ...(it.signs || []), it.implication, it.conduct,
    ...(it.avoid || []),
  ].filter(Boolean).join(" ").toLowerCase();
  return hay.includes(q.toLowerCase());
}

export default function MatrixRationalTab({ initialSection, initialId } = {}) {
  const [active, setActive] = useState(() => {
    const valid = SUB_TABS.find(t => t.key === initialSection);
    return valid ? valid.key : "principles";
  });
  const [q, setQ] = useState("");
  const [glowId, setGlowId] = useState(null);
  const cardRefs = useRef({});

  // Quando mudam os params (vindo de Ver no Matrix), navegamos para o card.
  useEffect(() => {
    if (!initialId) return;
    const valid = SUB_TABS.find(t => t.key === initialSection);
    if (valid) setActive(valid.key);
    // Aguarda render do card e scroll suave + glow.
    const t = setTimeout(() => {
      const el = cardRefs.current[initialId];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setGlowId(initialId);
        setTimeout(() => setGlowId(null), 2200);
      }
    }, 80);
    return () => clearTimeout(t);
  }, [initialSection, initialId]);

  const current = SUB_TABS.find(t => t.key === active) || SUB_TABS[0];
  const filtered = useMemo(() => current.data.filter(it => matchesQ(it, q)), [current, q]);

  // Helper para classes do card consoante glow ativo.
  const cardCls = (id, base) => glowId === id
    ? `${base} ring-2 ring-sky-400/60 shadow-[0_0_22px_rgba(56,189,248,0.4)] transition-shadow duration-300`
    : base;
  const setRef = (id) => (el) => { cardRefs.current[id] = el; };

  return (
    <div className="space-y-5" data-testid="matrix-rational">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-400 max-w-2xl">
          Conhecimento osteopático supervisionado — princípios, cadeias, padrões e testes em linguagem funcional não-diagnóstica.
          Esta secção sugere; a interpretação é da responsabilidade do osteopata.
        </p>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-500" />
          <Input
            data-testid="matrix-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Pesquisar nesta secção…"
            className="pl-9 w-72 bg-slate-900 border-slate-800 text-slate-100"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {SUB_TABS.map(t => {
          const I = t.icon;
          const on = t.key === active;
          return (
            <button
              key={t.key}
              type="button"
              data-testid={`matrix-subtab-${t.key}`}
              onClick={() => setActive(t.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-mono uppercase tracking-[0.15em] transition ${
                on ? "bg-sky-500/15 border-sky-500/40 text-sky-200" : "bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200"
              }`}
            >
              <I className="w-3.5 h-3.5" /> {t.label}
              <span className="text-[10px] opacity-70 ml-1">{t.data.length}</span>
            </button>
          );
        })}
      </div>

      {active === "principles" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map(it => (
            <div key={it.id} ref={setRef(it.id)} data-testid={`matrix-card-${it.id}`} className={cardCls(it.id, "rounded-xl border border-slate-800 bg-slate-900/50 p-4")}>
              <div className="text-slate-50 font-medium">{it.title}</div>
              <div className="text-sm text-slate-400 mt-1.5">{it.body}</div>
            </div>
          ))}
        </div>
      )}

      {active === "pillars" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map(it => (
            <div key={it.id} ref={setRef(it.id)} data-testid={`matrix-card-${it.id}`} className={cardCls(it.id, "rounded-xl border border-slate-800 bg-slate-900/50 p-4")}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-slate-50 font-medium">{it.title}</div>
              </div>
              <div className="text-sm text-slate-400 mt-1.5">{it.body}</div>
              {it.future && <div className="text-[11px] text-slate-500 italic mt-2">Futuro: {it.future}</div>}
            </div>
          ))}
        </div>
      )}

      {active === "chains" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map(it => (
            <div key={it.id} ref={setRef(it.id)} data-testid={`matrix-card-${it.id}`} className={cardCls(it.id, "rounded-xl border border-slate-800 bg-slate-900/50 p-4 space-y-2")}>
              <div className="text-slate-50 font-medium">{it.title}</div>
              {it.function && <div className="text-sm text-slate-400">{it.function}</div>}
              {it.regions?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {it.regions.map((r, i) => <span key={i} className="text-[10px] px-2 py-0.5 rounded-full border border-slate-700 text-slate-300">{r}</span>)}
                </div>
              )}
              {it.compensations?.length > 0 && (
                <div className="text-[12px] text-slate-400 mt-1">
                  <span className="text-slate-500 uppercase tracking-[0.15em] text-[10px]">Compensações: </span>
                  {it.compensations.join(" · ")}
                </div>
              )}
              {it.tests?.length > 0 && (
                <div className="text-[11px] text-slate-500 mt-1">
                  Testes sugeridos: {it.tests.map(id => findTest(id)?.title).filter(Boolean).join(" · ")}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {active === "patterns" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map(it => (
            <div key={it.id} ref={setRef(it.id)} data-testid={`matrix-card-${it.id}`} className={cardCls(it.id, "rounded-xl border border-violet-500/25 bg-violet-500/5 p-4 space-y-2")}>
              <div className="text-slate-50 font-medium">{it.title}</div>
              {it.description && <div className="text-sm text-slate-300">{it.description}</div>}
              {it.chains?.length > 0 && (
                <div className="text-[11px] text-slate-400">
                  Cadeias relacionadas: <span className="text-slate-200">{it.chains.map(id => findChain(id)?.title).filter(Boolean).join(" · ")}</span>
                </div>
              )}
              {it.regions?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {it.regions.map((r, i) => <span key={i} className="text-[10px] px-2 py-0.5 rounded-full border border-violet-500/30 text-violet-200 bg-violet-500/5">{r}</span>)}
                </div>
              )}
              {it.tests?.length > 0 && (
                <div className="text-[11px] text-slate-500">
                  Testes: {it.tests.map(id => findTest(id)?.title).filter(Boolean).join(" · ")}
                </div>
              )}
              {it.approach && (
                <div className="text-[12px] text-slate-300 italic mt-1">{it.approach}</div>
              )}
              <div className="text-[10px] text-amber-300/80 mt-1">Recomenda-se validação manual pelo osteopata.</div>
            </div>
          ))}
        </div>
      )}

      {active === "tests" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map(it => (
            <div key={it.id} ref={setRef(it.id)} data-testid={`matrix-card-${it.id}`} className={cardCls(it.id, "rounded-xl border border-slate-800 bg-slate-900/50 p-4 space-y-1.5")}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-slate-50 font-medium">{it.title}</div>
                {it.category && <span className="text-[10px] px-2 py-0.5 rounded-full border border-sky-500/30 text-sky-200 bg-sky-500/10 font-mono uppercase tracking-[0.12em]">{it.category}</span>}
              </div>
              {it.region && <div className="text-[11px] text-slate-400">Região: {it.region}</div>}
              {it.goal && <div className="text-sm text-slate-300"><span className="text-slate-500">Objetivo: </span>{it.goal}</div>}
              {it.how && <div className="text-[12px] text-slate-400"><span className="text-slate-500 text-[10px] uppercase tracking-[0.12em]">Execução: </span>{it.how}</div>}
              {it.interpretation && <div className="text-[12px] text-slate-300"><span className="text-slate-500 text-[10px] uppercase tracking-[0.12em]">Interpretação: </span>{it.interpretation}</div>}
              {it.contra && <div className="text-[11px] text-rose-300/90"><span className="opacity-80">Contraindicações: </span>{it.contra}</div>}
            </div>
          ))}
        </div>
      )}

      {active === "precautions" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map(it => (
            <div key={it.id} ref={setRef(it.id)} data-testid={`matrix-card-${it.id}`} className={cardCls(it.id, "rounded-xl border border-amber-500/25 bg-amber-500/5 p-4 space-y-2")}>
              <div className="flex items-start justify-between gap-2">
                <div className="text-slate-50 font-medium">{it.title}</div>
                <ShieldAlert className="w-4 h-4 text-amber-300/80 shrink-0" strokeWidth={1.5} />
              </div>
              {it.description && <div className="text-sm text-slate-300">{it.description}</div>}
              {it.signs?.length > 0 && (
                <div className="text-[12px] text-slate-400">
                  <span className="text-slate-500 uppercase tracking-[0.15em] text-[10px]">Sinais de alerta: </span>
                  {it.signs.join(" · ")}
                </div>
              )}
              {it.implication && (
                <div className="text-[12px] text-slate-300"><span className="text-slate-500 text-[10px] uppercase tracking-[0.12em]">Implicação clínica: </span>{it.implication}</div>
              )}
              {it.conduct && (
                <div className="text-[12px] text-emerald-200/90"><span className="text-emerald-400/80 text-[10px] uppercase tracking-[0.12em]">Conduta prudente: </span>{it.conduct}</div>
              )}
              {it.avoid?.length > 0 && (
                <div className="text-[11px] text-rose-300/90"><span className="opacity-80 text-[10px] uppercase tracking-[0.12em]">Evitar / adaptar: </span>{it.avoid.join(" · ")}</div>
              )}
              <div className="text-[10px] text-amber-300/80 pt-1 italic">A autoridade clínica permanece com o osteopata.</div>
            </div>
          ))}
        </div>
      )}

      {filtered.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-800 p-8 text-center text-slate-500 text-sm">
          Sem resultados para "{q}".
        </div>
      )}
    </div>
  );
}
